import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  Building2,
  CheckCircle2,
  Clock3,
  KeyRound,
  Loader2,
  LockKeyhole,
  MailCheck,
  RefreshCw,
  Send,
  Server,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  normalizeVipCode,
  submitVipCampaignRequest,
  validateVipCode,
  type VipCampaignStatus,
} from '../services/vipCampaign';
import './VipBoxLanding.css';

type VipPageState = 'loading' | 'error' | 'invalid' | VipCampaignStatus;

interface VipFormState {
  contactName: string;
  firm: string;
  email: string;
  phone: string;
  seats: string;
  displayName: string;
  wish: string;
  privacyAccepted: boolean;
  website: string;
}

const INITIAL_FORM: VipFormState = {
  contactName: '',
  firm: '',
  email: '',
  phone: '',
  seats: '1',
  displayName: '',
  wish: '',
  privacyAccepted: false,
  website: '',
};

const VALUE_POINTS = [
  {
    icon: Server,
    title: 'Ihr eigener Demo-Stack',
    text: 'Eine getrennte Premium-Demo unter demo-<slug>.arkorbit.at – auf Ihre Kanzlei vorbereitet.',
  },
  {
    icon: ShieldCheck,
    title: 'Kontrollierte Kanzlei-IT',
    text: 'Orbis verbindet Dokumente, Diktat und juristische Assistenz in einer nachvollziehbaren Umgebung.',
  },
  {
    icon: Building2,
    title: 'Persönlich eingerichtet',
    text: 'Anzeigename, Sitzanzahl und Ihr wichtigster Wunsch fließen direkt in die Einrichtung ein.',
  },
];

function useNoIndex(): void {
  useEffect(() => {
    const previousTitle = document.title;
    const existing = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const previousContent = existing?.getAttribute('content') ?? null;
    const robots = existing || document.createElement('meta');

    if (!existing) {
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex, nofollow, noarchive');
    document.title = 'Private VIP-Demo | ARKORBIT';

    return () => {
      document.title = previousTitle;
      if (!existing) {
        robots.remove();
      } else if (previousContent === null) {
        robots.removeAttribute('content');
      } else {
        robots.setAttribute('content', previousContent);
      }
    };
  }, []);
}

const VipBrandHeader: React.FC<{ isMock: boolean }> = ({ isMock }) => (
  <header className="vip-header">
    <Link to="/" className="vip-brand" aria-label="ARKORBIT Startseite">
      <img src="/arkorbit-logo-shield.svg" alt="" width={36} height={36} />
      <span>
        <strong>ARKORBIT</strong>
        <small>Private Einladung</small>
      </span>
    </Link>
    <div className="vip-header__badges">
      {isMock && <span className="vip-badge vip-badge--mock">DEV-MOCK · keine E-Mail</span>}
      <span className="vip-badge">Welle 01 · 10 Codes</span>
    </div>
  </header>
);

const VipFooter: React.FC = () => (
  <footer className="vip-footer">
    <span>© {new Date().getFullYear()} ARKORBIT AI Austria</span>
    <nav aria-label="Rechtliches">
      <Link to="/recht#datenschutz">Datenschutz</Link>
      <Link to="/recht">Impressum &amp; Rechtliches</Link>
      <a href="mailto:office@arkorbit.at">office@arkorbit.at</a>
    </nav>
  </footer>
);

interface StatusPanelProps {
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  tone?: 'default' | 'success' | 'warning';
}

const StatusPanel: React.FC<StatusPanelProps> = ({
  icon: Icon,
  eyebrow,
  title,
  children,
  action,
  tone = 'default',
}) => (
  <section className={`vip-status vip-status--${tone}`} aria-live="polite">
    <span className="vip-status__icon" aria-hidden>
      <Icon size={30} aria-hidden />
    </span>
    <p className="vip-eyebrow">{eyebrow}</p>
    <h1>{title}</h1>
    <div className="vip-status__copy">{children}</div>
    {action && <div className="vip-status__action">{action}</div>}
  </section>
);

const VipBoxLanding: React.FC = () => {
  useNoIndex();
  const [searchParams] = useSearchParams();
  const code = useMemo(
    () => normalizeVipCode(searchParams.get('code') || ''),
    [searchParams],
  );
  const [pageState, setPageState] = useState<VipPageState>('loading');
  const [slot, setSlot] = useState<number | undefined>();
  const [isMock, setIsMock] = useState(false);
  const [invalidMessage, setInvalidMessage] = useState(
    'Dieser Code gehört nicht zur aktiven VIP-Welle oder ist nicht vollständig.',
  );
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [form, setForm] = useState<VipFormState>(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formFeedback, setFormFeedback] = useState('');

  useEffect(() => {
    let cancelled = false;
    setPageState('loading');
    setFormFeedback('');

    validateVipCode(code)
      .then((result) => {
        if (cancelled) return;
        setIsMock(result.isMock);
        setSlot(result.slot);
        if (!result.valid || !result.status) {
          setInvalidMessage(
            !code
              ? 'In der Adresse fehlt der Parameter code=ARK-VIP-01 … bzw. 01-ARK-VIP-01 …. Bitte den kompletten Link öffnen (nicht nur /vip).'
              : result.message ||
                  'Dieser Code gehört nicht zur aktiven VIP-Welle oder ist nicht vollständig.',
          );
          setPageState('invalid');
          return;
        }
        setPageState(result.status);
      })
      .catch(() => {
        if (cancelled) return;
        setPageState('error');
      });

    return () => {
      cancelled = true;
    };
  }, [code, validationAttempt]);

  const setField = <K extends keyof VipFormState>(field: K, value: VipFormState[K]) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    const seats = Number.parseInt(form.seats, 10);
    if (!Number.isFinite(seats) || seats < 1) {
      setFormFeedback('Bitte geben Sie mindestens einen Sitz an.');
      return;
    }
    if (!form.privacyAccepted) {
      setFormFeedback('Bitte bestätigen Sie die zweckgebundene Verarbeitung Ihrer Angaben.');
      return;
    }

    setIsSubmitting(true);
    setFormFeedback('');
    try {
      const result = await submitVipCampaignRequest({
        code,
        contactName: form.contactName.trim(),
        firm: form.firm.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        seats,
        displayName: form.displayName.trim(),
        wish: form.wish.trim(),
        privacyAccepted: form.privacyAccepted,
        website: form.website,
      });
      setIsMock(result.isMock);

      if (result.status && result.status !== 'unused') {
        setPageState(result.status);
        return;
      }

      setFormFeedback(
        result.message || 'Die Anfrage konnte nicht sicher übermittelt werden. Bitte versuchen Sie es erneut.',
      );
    } catch {
      setFormFeedback(
        'Die Verbindung ist gerade nicht verfügbar. Bitte versuchen Sie es in wenigen Minuten erneut.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStatus = () => {
    if (pageState === 'loading') {
      return (
        <StatusPanel icon={Loader2} eyebrow="VIP-Code" title="Einladung wird geprüft …">
          <p>Die Gültigkeit wird sicher auf dem ARKORBIT-Server geprüft.</p>
        </StatusPanel>
      );
    }

    if (pageState === 'error') {
      return (
        <StatusPanel icon={AlertCircle} eyebrow="Verbindung" title="Prüfung gerade nicht möglich" tone="warning">
          <p>Der VIP-Code konnte nicht geprüft werden. Es wurden keine Angaben übertragen.</p>
          <button
            type="button"
            className="vip-button vip-button--primary"
            onClick={() => setValidationAttempt((attempt) => attempt + 1)}
          >
            <RefreshCw size={18} aria-hidden />
            Erneut prüfen
          </button>
        </StatusPanel>
      );
    }

    if (pageState === 'invalid') {
      return (
        <StatusPanel
          icon={LockKeyhole}
          eyebrow="Private Einladung"
          title="VIP-Code ungültig"
          action={
            <Link to="/" className="vip-button vip-button--secondary">
              Zu arkorbit.at
              <ArrowRight size={18} aria-hidden />
            </Link>
          }
        >
          <p>{invalidMessage}</p>
        </StatusPanel>
      );
    }

    if (pageState === 'requested') {
      return (
        <StatusPanel
          icon={MailCheck}
          eyebrow={`VIP ${code}`}
          title="Anfrage bereits eingegangen"
          tone="success"
          action={
            <a href="mailto:office@arkorbit.at" className="vip-button vip-button--secondary">
              office@arkorbit.at
            </a>
          }
        >
          <p>
            Danke – dieser Code ist reserviert. ARKORBIT meldet sich innerhalb von
            <strong> 12 Stunden an Arbeitstagen (Mo–Fr)</strong>. Anfragen am Wochenende
            werden ab Montag bearbeitet.
          </p>
          <div className="vip-status__note">
            <Clock3 size={19} aria-hidden />
            Bitte kein zweites Formular senden.
          </div>
        </StatusPanel>
      );
    }

    return (
      <StatusPanel
        icon={KeyRound}
        eyebrow={`VIP ${code}`}
        title="Aktivierung bereits eingerichtet"
        action={
          <Link to="/" className="vip-button vip-button--secondary">
            Zu arkorbit.at
            <ArrowRight size={18} aria-hidden />
          </Link>
        }
      >
        <p>
          Dieser Code ist nicht mehr für eine neue Anfrage gültig, weil die Aktivierung
          bereits eingerichtet oder zugestellt wurde.
        </p>
      </StatusPanel>
    );
  };

  return (
    <main className="vip-page">
      <div className="vip-page__grid" aria-hidden />
      <div className="vip-page__glow vip-page__glow--one" aria-hidden />
      <div className="vip-page__glow vip-page__glow--two" aria-hidden />

      <div className="vip-shell">
        <VipBrandHeader isMock={isMock} />

        {pageState !== 'unused' ? (
          <div className="vip-status-wrap">{renderStatus()}</div>
        ) : (
          <>
            <section className="vip-hero">
              <div className="vip-hero__copy">
                <p className="vip-eyebrow">
                  <Sparkles size={16} aria-hidden />
                  Persönliche Einladung · Code {String(slot || '').padStart(2, '0')} von 10
                </p>
                <h1>
                  Ihre Kanzlei wurde für die erste
                  <span> ARKORBIT VIP‑Welle</span> ausgewählt.
                </h1>
                <p className="vip-hero__lead">
                  Der gescannte Code öffnet genau eine Anfrage für eine persönlich
                  eingerichtete Orbis-Premium-Demo. Nutzen Sie ihn, bevor die erste Welle
                  vergeben ist.
                </p>
                <a href="#vip-anfrage" className="vip-button vip-button--primary">
                  VIP-Demo reservieren
                  <ArrowRight size={18} aria-hidden />
                </a>
                <div className="vip-hero__trust">
                  <ShieldCheck size={19} aria-hidden />
                  Serverseitig geprüfter Einzelcode · keine Newsletter-Anmeldung
                </div>
              </div>

              <aside className="vip-invite-card" aria-label="VIP-Einladung">
                <span className="vip-invite-card__label">ARKORBIT PRIVATE ACCESS</span>
                <div className="vip-invite-card__slot">{String(slot || '').padStart(2, '0')}</div>
                <strong>Welle Eins</strong>
                <p>Orbis · persönliche Einrichtung · 14 Tage Demo</p>
                <div className="vip-invite-card__code">{code}</div>
              </aside>
            </section>

            <section className="vip-value" aria-labelledby="vip-value-title">
              <header>
                <p className="vip-eyebrow">Was ARKORBIT für Kanzleien baut</p>
                <h2 id="vip-value-title">Nicht nur ansehen. Im eigenen Setup erleben.</h2>
              </header>
              <div className="vip-value__grid">
                {VALUE_POINTS.map(({ icon: Icon, title, text }) => (
                  <article key={title}>
                    <span aria-hidden>
                      <Icon size={22} aria-hidden />
                    </span>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </article>
                ))}
              </div>
            </section>

            <section className="vip-form-section" id="vip-anfrage" aria-labelledby="vip-form-title">
              <div className="vip-form-section__intro">
                <p className="vip-eyebrow">Einmalige Reservierung</p>
                <h2 id="vip-form-title">Premium-Demo einrichten lassen</h2>
                <p>
                  Nach der Anfrage prüft Andreas die Angaben und richtet den Demo-Stack im
                  MVP manuell ein. Die Zugangsdaten folgen persönlich.
                </p>
                <ul>
                  <li>
                    <CheckCircle2 size={18} aria-hidden />
                    Aktivierungsfenster: 14 Tage ab Zugangsdaten-Mail
                  </li>
                  <li>
                    <CheckCircle2 size={18} aria-hidden />
                    Demolaufzeit: 14 Tage ab erster Passwortänderung
                  </li>
                  <li>
                    <CheckCircle2 size={18} aria-hidden />
                    Rückmeldung binnen 12 Stunden an Arbeitstagen
                  </li>
                </ul>
              </div>

              <form className="vip-form" onSubmit={handleSubmit} autoComplete="on">
                <div className="vip-form__row">
                  <label>
                    Name Ansprechpartner <span aria-hidden>*</span>
                    <input
                      name="name"
                      value={form.contactName}
                      onChange={(event) => setField('contactName', event.target.value)}
                      autoComplete="name"
                      required
                    />
                  </label>
                  <label>
                    Kanzlei <span aria-hidden>*</span>
                    <input
                      name="organization"
                      value={form.firm}
                      onChange={(event) => setField('firm', event.target.value)}
                      autoComplete="organization"
                      required
                    />
                  </label>
                </div>

                <div className="vip-form__row">
                  <label>
                    E-Mail <span aria-hidden>*</span>
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={(event) => setField('email', event.target.value)}
                      autoComplete="email"
                      required
                    />
                  </label>
                  <label>
                    Telefon <small>optional</small>
                    <input
                      name="tel"
                      type="tel"
                      value={form.phone}
                      onChange={(event) => setField('phone', event.target.value)}
                      autoComplete="tel"
                    />
                  </label>
                </div>

                <div className="vip-form__row vip-form__row--compact">
                  <label>
                    Anzahl Sitze <span aria-hidden>*</span>
                    <input
                      name="seats"
                      type="number"
                      min="1"
                      max="500"
                      inputMode="numeric"
                      value={form.seats}
                      onChange={(event) => setField('seats', event.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Box-Code
                    <input name="code" value={code} readOnly aria-readonly="true" />
                  </label>
                </div>

                <label>
                  Anzeigename der Kanzlei <span aria-hidden>*</span>
                  <small>So erscheint der Name in der Demo – Umlaute sind möglich.</small>
                  <input
                    name="displayName"
                    value={form.displayName}
                    onChange={(event) => setField('displayName', event.target.value)}
                    required
                  />
                </label>

                <label>
                  Ihr wichtigster Wunsch an ARKORBIT <span aria-hidden>*</span>
                  <textarea
                    name="wish"
                    rows={4}
                    value={form.wish}
                    onChange={(event) => setField('wish', event.target.value)}
                    placeholder="Welche Aufgabe soll die Demo für Ihre Kanzlei konkret zeigen?"
                    required
                  />
                </label>

                <label className="vip-form__privacy">
                  <input
                    name="privacyAccepted"
                    type="checkbox"
                    checked={form.privacyAccepted}
                    onChange={(event) => setField('privacyAccepted', event.target.checked)}
                    required
                  />
                  <span>
                    Ich stimme zu, dass ARKORBIT meine Angaben ausschließlich zur Einrichtung
                    der Premium-Demo und zur Kontaktaufnahme dazu verarbeitet. Hinweise in der{' '}
                    <Link to="/recht#datenschutz">Datenschutzerklärung</Link>. <span aria-hidden>*</span>
                  </span>
                </label>

                <label className="vip-form__honeypot" aria-hidden>
                  Website
                  <input
                    name="website"
                    value={form.website}
                    onChange={(event) => setField('website', event.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </label>

                {formFeedback && (
                  <p className="vip-form__feedback" role="alert">
                    {formFeedback}
                  </p>
                )}

                <button
                  type="submit"
                  className="vip-button vip-button--primary vip-form__submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="vip-spin" aria-hidden />
                      Anfrage wird gesichert …
                    </>
                  ) : (
                    <>
                      <Send size={18} aria-hidden />
                      VIP-Demo jetzt anfragen
                    </>
                  )}
                </button>
                <p className="vip-form__fineprint">
                  Die Anfrage ist noch kein Vertrag und löst keine Zahlung aus.
                </p>
              </form>
            </section>
          </>
        )}

        <VipFooter />
      </div>
    </main>
  );
};

export default VipBoxLanding;
